<template>
  <div class="pop-insights-container">
    <div class="pop-insights-wrapper">
      <h1>Possibility of Passing</h1>
      <div class="first-column-wrapper">
        <div class="pop-card">
          <pop-bar-chart
            :level="activeLevel"
            :next-update-progress="readinessState?.nextUpdateProgress || 0"
            :next-update-total="readinessState?.nextUpdateTotal || 0"
            data-test="pop-bar-chart"
          />
        </div>
        <div
          class="desktop-only pop-card text-tertiary"
        >
          <p class="text-sm">
            This shows your estimated chance of passing the exam. We base it on
            your practice question answers and simulation results.
          </p>
          <p class="text-sm">
            The system continuously adapts, focusing on your strengths, weaknesses,
            and consistency across all exam categories.
          </p>
        </div>
      </div>
      <div class="pop-card">
        <div v-if="activeLevel">
          <h2>What your score means</h2>
          <p class="text-md">
            <span
              class="font-semibold"
              :style="activeLevelColor ? { color: activeLevelColor } : {}"
              data-test="active-level-name"
            >{{ activeLevel.name }}: </span>
            <span>{{ activeLevel.description }}</span>
          </p>
        </div>
        <div v-if="activeLevel" class="next-step">
          <div class="next-step-header">
            <icon name="lightbulb-2" />
            <span class="text-xl font-semibold">Next Step</span>
          </div>
          <p class="text-md next-step-description">
            {{ activeLevel.nextStepDescription }}
          </p>
          <div class="next-step-action">
            <router-link
              :to="defaultQuestionPath"
              class="answer-questions-link text-sm font-semibold"
              data-test="answer-questions-link"
              @click="trackAnswerQuestions"
            >
              Answer Questions &rarr;
            </router-link>
          </div>
        </div>
        <div class="targets-section" data-test="targets-section">
          <h2>Targets for Success</h2>
          <p class="text-md targets-description">
            98% of {{ appName }} users who hit both targets pass. Focus on answering practice questions and your simulation score to maximize your chances.
          </p>
          <div class="targets-table">
            <div class="targets-row" data-test="target-practice-questions">
              <span class="targets-label text-md font-semibold">Tagged Practice Questions</span>
              <span class="targets-value text-md">{{ readinessState?.targetPracticeQuestions }}</span>
            </div>
            <div
              v-if="readinessState?.targetSimulationScore"
              class="targets-row"
              data-test="target-simulation-score"
            >
              <span class="targets-label text-md font-semibold">
                Simulation Score
                <tooltip
                  message="Available on desktop only"
                  placement="top"
                  data-test="sim-info-tooltip"
                >
                  <button
                    type="button"
                    class="sim-info-button"
                    data-test="sim-info-button"
                    aria-label="Simulation score info"
                    @click="trackSimInfo"
                  >
                    <icon name="help-circle" class="sim-info-icon" data-test="sim-info-icon" />
                  </button>
                </tooltip>
              </span>
              <span class="targets-value text-md">{{ readinessState?.targetSimulationScore }}</span>
            </div>
          </div>
        </div>
        <div class="faq-section">
          <h2 class="faq-title">
            Frequently Asked Questions
          </h2>
          <div class="faq-list">
            <div
              v-for="(faq, index) in faqs"
              :key="faq.id"
              class="faq-item"
              :class="{ 'faq-item--border': index > 0 }"
            >
              <button
                class="faq-item-header"
                data-test="faq-toggle"
                :aria-expanded="openFaqIds.has(faq.id)"
                :aria-controls="`faq-answer-${faq.id}`"
                @click="toggleFaq(faq.id)"
              >
                <span class="faq-question text-md font-semibold">{{ faq.question }}</span>
                <icon
                  :name="openFaqIds.has(faq.id) ? 'minus-circle' : 'plus-circle'"
                  class="faq-icon"
                />
              </button>
              <p
                v-if="openFaqIds.has(faq.id)"
                :id="`faq-answer-${faq.id}`"
                class="faq-answer text-md"
                data-test="faq-answer"
              >
                {{ faq.answer }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import PopBarChart from '@components/dashboard-widgets/PopBarChart.vue';
import Icon from '@components/singletons/icon.vue';
import Tooltip from '@components/singletons/tooltip.vue';
import { useDefaultQuestionPath } from '@/composables/useDefaultQuestionPath';
import { useAnalytics } from '@/composables/useAnalytics';
import type { PopReadinessState, PopReadinessLevel } from '@/types/models';

const store = useStore();
const { defaultQuestionPath } = useDefaultQuestionPath();
const { events: analyticsEvents } = useAnalytics();

const appName = computed<string>(() => store.getters['app/appName']);
const readinessState = computed<PopReadinessState | null>(() => store.getters['popReadiness/readinessState'] || null);
const levels = computed<PopReadinessLevel[]>(() => store.getters['popReadiness/levels']);

const activeLevel = computed<PopReadinessLevel | null>(() => {
  if (!readinessState.value?.popReadinessLevelId) return null;
  return levels.value.find(l => l.id === readinessState.value?.popReadinessLevelId) || null;
});

const LEVEL_COLORS: Record<string, string> = {
  'Low': 'var(--utility-error-500)',
  'Borderline': 'var(--utility-warning-500)',
  'High': 'var(--utility-success-500)',
  'Very High': 'var(--utility-success-700)',
};

const activeLevelColor = computed<string | null>(() => {
  if (!activeLevel.value?.name) return null;
  return LEVEL_COLORS[activeLevel.value.name] || null;
});

const faqs = computed(() => store.getters['popReadiness/faqs']);

const openFaqIds = ref<Set<number>>(new Set());

watch(faqs, (newFaqs) => {
  if (newFaqs?.length && openFaqIds.value.size === 0) {
    openFaqIds.value = new Set([newFaqs[0].id]);
  }
}, { immediate: true });

const toggleFaq = (id: number) => {
  const next = new Set(openFaqIds.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  openFaqIds.value = next;
};

const trackAnswerQuestions = () => {
  analyticsEvents.popTappedAnswerQuestions();
};

const trackSimInfo = () => {
  analyticsEvents.popViewedSimInfo();
};

onMounted(() => {
  store.dispatch('popReadiness/fetchReadinessState').catch(() => {});
});
</script>

<style lang="scss" scoped>
.pop-insights-container {
  background-color: var(--bg-secondary);

  .pop-insights-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 24px;

    @include breakpoint(desktop) {
      flex-direction: row;
    }
  }

  h1 {
    margin-top: 0;
    margin-bottom: 0;
    font-family: var(--Fontfamilyfont-family-body);
    font-size: var(--Fontsizetext-xl);
    line-height: var(--Lineheighttext-xl);
    color: var(--text-primary);

    @include breakpoint(desktop) {
      display: none;
    }
  }
}

.first-column-wrapper {
  @include breakpoint(desktop) {
    max-width: 400px;
  }
}

.pop-card {
  background: white;
  border: 1px solid var(--border-secondary);
  border-radius: var(--radius-xl);
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  padding: 24px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @include breakpoint(desktop) {
    min-width: 371px;
    max-width: 632px;
  }

  &.desktop-only {
    display: none;

    @include breakpoint(desktop) {
      display: block;
      margin-top: rem-calc(20);
      background-color: var(--bg-tertiary);

      p:first-child {
        margin-top: 0;
      }

      p:last-child {
        margin-bottom: 0;
      }
    }
  }

  h2 {
    margin-top: 0;
    font-family: var(--Fontfamilyfont-family-body);
    font-size: var(--Fontsizetext-xl);
    font-weight: var(--Fontweightfont-weight-semibold);
    line-height: var(--Lineheighttext-xl);
    color: var(--text-primary);
  }
}

.next-step {
  margin-top: 16px;
  padding: var(--spacing-2xl);
  background-color: var(--bg-light);
  border-radius: var(--radius-2xl);
}

.next-step-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary);
}

.next-step-description {
  color: var(--text-tertiary);
  margin: 4px 0 12px;
}

.next-step-action {
  display: flex;
  justify-content: flex-end;
}

.answer-questions-link {
  color: var(--Colors-Brand-brand-700, #004EEB);
  text-decoration: none;
}

.targets-section {
  margin-top: 16px;
}

.targets-description {
  color: var(--text-tertiary);
  margin: 0 0 16px;
}

.targets-table {
  display: flex;
  flex-direction: column;
}

.targets-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: rem-calc(16);
  border: 1px solid var(--Colors-Border-border-secondary);

  &:first-child {
    background-color: var(--bg-light);
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }

  &:last-child {
    border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  }

  &:last-child:not(:first-child) {
    border-top: none;
  }

  &:only-child {
    background-color: var(--bg-light);
    border-radius: var(--radius-lg);
  }
}

.targets-label {
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.sim-info-button {
  all: unset;
  display: inline-flex;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--Colors-Brand-brand-700, #004EEB);
    outline-offset: 2px;
    border-radius: var(--radius-xs);
  }
}

.sim-info-icon {
  width: 16px;
  height: 16px;
  color: var(--text-quaternary);
}

.targets-value {
  color: var(--text-primary);
}

.faq-section {
  margin-top: 16px;
}

.faq-title {
  margin-top: 0;
  margin-bottom: 16px;
  font-family: var(--Fontfamilyfont-family-body);
  font-size: var(--Fontsizedisplay-xs);
  line-height: var(--Lineheightdisplay-xs);
  color: var(--text-primary);
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.faq-item--border {
  border-top: 1px solid var(--border-secondary);
  padding-top: 24px;
}

.faq-item-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
}

.faq-question {
  flex: 1;
  color: var(--text-primary);
}

.faq-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.faq-answer {
  color: var(--text-tertiary);
  margin: 4px 0 0;
  white-space: pre-line;
}
</style>
